import { type FC, useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import { FormBuilder } from "@/shared/components/Form";
import { ErrorMessage } from "@/shared/components";
import { validateWithZod } from "@/shared/utils/zod";
import {
  getErrorMessage,
  getGraphQLErrorCode,
} from "@/shared/utils/errorCodes";
import {
  useBillingSellerQuery,
  useUpdateBillingSellerMutation,
  type BillingSeller,
} from "@/admin/entities/billing";
import {
  makeBillingSellerSchema,
  makeBillingSellerValidationSchema,
} from "../model";

function mapBillingSellerToFormValues(seller?: BillingSeller | null) {
  if (!seller) {
    return {
      companyName: "",
      registrationNumber: "",
      vatNumber: "",
      address: "",
      email: "",
      invoiceNumberPrefix: "",
      invoiceLanguage: "en",
      vatNote: "",
      signerName: "",
      signerPosition: "",
      logoId: null,
      brandColor: "",
      bankAccounts: [],
    };
  }

  return {
    companyName: seller.companyName,
    registrationNumber: seller.registrationNumber,
    vatNumber: seller.vatNumber,
    address: seller.address,
    email: seller.email,
    invoiceNumberPrefix: seller.invoiceNumberPrefix,
    invoiceLanguage: seller.invoiceLanguage,
    vatNote: seller.vatNote,
    signerName: seller.signerName,
    signerPosition: seller.signerPosition,
    logoId: seller.logo?.id ?? null,
    brandColor: seller.brandColor,
    bankAccounts: seller.bankAccounts.map((account) => ({
      currency: account.currency,
      beneficiary: account.beneficiary,
      bankName: account.bankName,
      bankAddress: account.bankAddress,
      iban: account.iban,
      swift: account.swift,
      instructions: account.instructions,
    })),
  };
}

/**
 * Настройки продавца ottkit для печати счетов (спека §3). Документ один на
 * инстанс: формы создания нет, только редактирование уже существующей
 * заглушки, которую бэкенд заводит сам при первом обращении.
 */
export const BillingSellerSettings: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error, refetch } = useBillingSellerQuery();

  const [updateBillingSeller, { loading: updating }] =
    useUpdateBillingSellerMutation({
      onCompleted: () => {
        enqueueSnackbar("Реквизиты продавца сохранены", {
          variant: "success",
        });
        refetch();
      },
      onError: (mutationError) => {
        enqueueSnackbar(
          getErrorMessage(
            getGraphQLErrorCode(mutationError),
            mutationError.message
          ),
          { variant: "error" }
        );
      },
    });

  const schema = useMemo(() => makeBillingSellerSchema(), []);
  const validate = useMemo(
    () => validateWithZod(makeBillingSellerValidationSchema()),
    []
  );
  const initialValues = useMemo(
    () => mapBillingSellerToFormValues(data?.billingSeller),
    [data]
  );

  const handleSubmit = async (values: Record<string, any>) => {
    await updateBillingSeller({ variables: { input: values } });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <Card>
      <CardHeader title="Реквизиты продавца" sx={{ px: 2, pt: 2, pb: 0 }} />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        {!data?.billingSeller.filled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Пока реквизиты не заполнены, счета клиентам не выставляются
            автоматически.
          </Alert>
        )}
        <FormBuilder
          formId="billing-seller-settings-form"
          schema={schema}
          validate={validate}
          initialValues={initialValues}
          editData={data?.billingSeller}
          onSubmit={handleSubmit}
          submitButton
          resetButton={false}
          submitButtonText="Сохранить"
          disabled={updating}
          spacing={2}
          actionsAlign="end"
        />
      </CardContent>
    </Card>
  );
};
